import { Link, useLocation, useParams } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { money, shortDate } from '../lib/format';
import { ExploreTabs } from '../components/ExploreTabs';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { Alert, Card, ErrorBlock, LoadingBlock } from '../components/ui';
import { ReportListing } from '../components/ReportListing';
import '../styles/public.css';

/**
 * One item's page.
 *
 * Reachable while approved OR sold — a sold item keeps its URL, and says so at
 * the top, because the person who followed the link is owed the answer rather
 * than a 404 and a phone call to find out.
 */

interface Item {
  id: string; slug: string; title: string; description: string | null;
  price: string | null; condition: string | null;
  county: string; town: string | null; seller_name: string;
  phone: string; whatsapp: string | null; email: string | null;
  status: 'approved' | 'sold'; is_featured: boolean; expires_at: string;
  category_slug: string; category_name: string;
}

const CONDITION: Record<string, string> = {
  new: 'New', like_new: 'Like new', good: 'Good', fair: 'Fair', for_parts: 'For parts',
};

const priceLabel = (p: string | null) =>
  p === null ? 'Contact for price' : Number(p) === 0 ? 'Free' : money(p);

const waLink = (n: string) => `https://wa.me/${n.replace(/[^0-9]/g, '')}`;

export function MarketplaceItem() {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const view = useApi<{ item: Item }>(slug ? `/api/v1/public/marketplace/${slug}` : null);

  return (
    <>
      <PublicHeader />
      <main className="page public-page">
        <ExploreTabs path={pathname} />

        {view.loading ? <LoadingBlock />
          : view.error ? <ErrorBlock error={view.error} onRetry={view.reload} />
          : !view.data ? null
          : (() => {
            const i = view.data.item;
            const sold = i.status === 'sold';
            return (
              <>
                {sold && (
                  <div style={{ marginBottom: 16 }}>
                    <Alert tone="warning">
                      This item has been sold. It is kept here so the link still works.
                    </Alert>
                  </div>
                )}

                <div className="page-head">
                  <div>
                    <p className="muted" style={{ margin: 0 }}>
                      <Link to={`/marketplace?category=${i.category_slug}`}>{i.category_name}</Link>
                    </p>
                    <h1 className="page-title">{i.title}</h1>
                    <p className="page-sub">
                      {i.town ? `${i.town}, ` : ''}{i.county}
                      {i.condition ? ` · ${CONDITION[i.condition] ?? i.condition}` : ''}
                    </p>
                  </div>
                </div>

                <div className="grid grid-2" style={{ alignItems: 'start' }}>
                  <Card title="Description">
                    <p style={{ marginTop: 0, fontSize: 'var(--fs-h3, 22px)', fontWeight: 600 }}>
                      {priceLabel(i.price)}
                    </p>
                    <p style={{ marginBottom: 0 }}>
                      {i.description || 'The seller has not added a description.'}
                    </p>
                  </Card>

                  <Card title={sold ? 'Seller' : 'Contact the seller'}>
                    <p style={{ marginTop: 0 }}>{i.seller_name}</p>
                    {sold ? (
                      <p className="muted" style={{ marginBottom: 0 }}>
                        This item is no longer available, so the seller's number is not shown.
                      </p>
                    ) : (
                      <>
                        <p>
                          <a href={`tel:${i.phone}`}
                            style={{ fontSize: 'var(--fs-h3, 20px)', fontWeight: 600 }}>
                            {i.phone}
                          </a>
                        </p>
                        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <a className="btn btn-primary" href={`tel:${i.phone}`}>Call seller</a>
                          {i.whatsapp && (
                            <a className="btn btn-secondary" href={waLink(i.whatsapp)}
                              target="_blank" rel="noreferrer">WhatsApp</a>
                          )}
                          {i.email && (
                            <a className="btn btn-ghost" href={`mailto:${i.email}`}>Email</a>
                          )}
                        </div>
                        <p className="muted" style={{ marginBottom: 0, marginTop: 16 }}>
                          Listed until {shortDate(i.expires_at)}.
                        </p>
                      </>
                    )}
                  </Card>
                </div>

                {/*
                  * Said plainly, because it is true and because somebody
                  * arranging to meet a stranger about a sofa should read it.
                  * §16: the platform introduces people and stays out of the
                  * deal — no payment passes through NyumbaSuite, so there is
                  * nothing here to refund and we should not imply otherwise.
                  */}
                <p className="muted" style={{ marginTop: 24 }}>
                  NyumbaSuite is not part of this sale. Agree the price directly, meet somewhere
                  public, and check the item before you pay.
                </p>

                {/* §38. Matters more here than on a business listing: the
                    warning above tells a buyer to be careful, and this is the
                    only way for one who was not to warn the next. */}
                <ReportListing targetType="item" targetId={i.id} what="item" />
              </>
            );
          })()}
      </main>
      <SiteFooter />
    </>
  );
}
